import { InventoryHistory } from 'types/inventory';
import { drawPdfText } from '../pdfUtils';
import jsPDF from 'jspdf';
import { ReportCompanyHeader } from '../reportHeader';
import { drawWrappedCenteredAddress } from '../reportPdfHeader';

const PAGE_WIDTH = 210;
const PAGE_HEIGHT = 297;
const MARGIN = 15;
const LINE_HEIGHT = 6;
const MAX_Y = PAGE_HEIGHT - MARGIN - 10;

const HEADER_GAP_TIN_TO_TITLE_MM = 7;
/** Space from title baseline to body start (matches previous fixed layout). */
const HEADER_GAP_TITLE_TO_BODY_MM = 12;

interface PDFContext {
  pdf: jsPDF;
  y: number;
  totalPages: number;
  reportTitle: string;
  fontFamily: string;
  isEditable: boolean;
}

const checkPageBreak = (
  ctx: PDFContext,
  requiredHeight: number = LINE_HEIGHT,
): void => {
  if (ctx.y + requiredHeight > MAX_Y) {
    ctx.pdf.addPage();
    ctx.y = MARGIN;
    ctx.totalPages++;
  }
};

const drawReportHeader = (
  ctx: PDFContext,
  companyHeader: ReportCompanyHeader,
): void => {
  const pdf = ctx.pdf;
  const xCenter = PAGE_WIDTH / 2;

  // Company Name
  const nameWidth = pdf.getTextWidth(companyHeader.registeredBusinessName);
  drawPdfText(pdf, companyHeader.registeredBusinessName, xCenter, ctx.y, {
    fontSize: 11,
    fontStyle: 'bold',
    textAlign: 'center',
    fontName: ctx.fontFamily,
    width: nameWidth,
    editable: ctx.isEditable,
  });

  const addrFirstY = ctx.y + 4.5;
  const tinY = drawWrappedCenteredAddress(
    pdf,
    xCenter,
    addrFirstY,
    companyHeader.registeredBusinessAddress,
    {
      fontSize: 10,
      fontFamily: ctx.fontFamily,
      isEditable: ctx.isEditable,
    },
  );

  const tinText = `TIN: ${companyHeader.registeredBusinessTin}`;
  const tinWidth = pdf.getTextWidth(tinText);
  drawPdfText(pdf, tinText, xCenter, tinY, {
    fontSize: 10,
    textAlign: 'center',
    fontName: ctx.fontFamily,
    width: tinWidth,
    editable: ctx.isEditable,
  });

  const titleY = tinY + HEADER_GAP_TIN_TO_TITLE_MM;
  const titleWidth = pdf.getTextWidth(ctx.reportTitle);
  drawPdfText(pdf, ctx.reportTitle, xCenter, titleY, {
    fontSize: 18,
    fontStyle: 'bold',
    textAlign: 'center',
    fontName: ctx.fontFamily,
    width: titleWidth,
    editable: ctx.isEditable,
  });

  ctx.y = titleY + HEADER_GAP_TITLE_TO_BODY_MM;
};

export const generateInventoryReportPDF = (
  data: InventoryHistory[],
  returnPDF: boolean = false,
  fontFamily: 'times' | 'courier' | 'arial' = 'arial',
  reportTitle: string = 'Inventory Report',
  companyHeader: ReportCompanyHeader,
  isEditable: boolean = true,
): jsPDF | void => {
  const pdf = new jsPDF('p', 'mm', 'a4');

  const ctx: PDFContext = {
    pdf,
    y: MARGIN,
    totalPages: 1,
    reportTitle,
    fontFamily:
      fontFamily.toLowerCase() === 'arial'
        ? 'helvetica'
        : fontFamily.toLowerCase(),
    isEditable,
  };

  drawReportHeader(ctx, companyHeader);

  // Table Headers
  const colWidths = [45, 60, 25, 25, 25];
  const headers = ['Date & Time', 'Product', 'Type', 'Quantity', 'Remaining'];

  pdf.setLineWidth(0.3);
  pdf.line(MARGIN, ctx.y - 4, PAGE_WIDTH - MARGIN, ctx.y - 4);

  let currentX = MARGIN;
  headers.forEach((h, i) => {
    const align = i >= 3 ? 'right' : 'left';
    const fieldX = align === 'right' ? currentX + colWidths[i] : currentX;
    drawPdfText(pdf, h, fieldX, ctx.y, {
      fontSize: 9,
      fontStyle: 'bold',
      textAlign: align,
      fontName: ctx.fontFamily,
      width: colWidths[i] - 2,
      editable: ctx.isEditable,
    });
    currentX += colWidths[i];
  });

  ctx.y += 4;
  pdf.line(MARGIN, ctx.y - 2, PAGE_WIDTH - MARGIN, ctx.y - 2);
  ctx.y += 4;

  // Table Rows
  data.forEach((entry) => {
    checkPageBreak(ctx, 8);
    let rowX = MARGIN;

    // Date & Time
    const date = new Date(entry.createdAt);
    const dateStr = date.toLocaleDateString();
    const timeStr = date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
    const dateTimeStr = `${dateStr} ${timeStr}`;
    drawPdfText(pdf, dateTimeStr, rowX, ctx.y, {
      fontSize: 8,
      fontName: ctx.fontFamily,
      width: colWidths[0] - 2,
      editable: ctx.isEditable,
    });
    rowX += colWidths[0];

    // Product
    drawPdfText(pdf, entry.product.name || 'Unknown', rowX, ctx.y, {
      fontSize: 8,
      fontName: ctx.fontFamily,
      width: colWidths[1] - 5,
      editable: ctx.isEditable,
    });
    rowX += colWidths[1];

    // Type
    drawPdfText(pdf, entry.changeType, rowX, ctx.y, {
      fontSize: 8,
      fontName: ctx.fontFamily,
      width: colWidths[2] - 2,
      editable: ctx.isEditable,
    });
    rowX += colWidths[2];

    // Quantity
    const qty =
      (parseFloat(entry.quantityChange) || 0) >= 0
        ? `+${entry.quantityChange}`
        : entry.quantityChange;
    drawPdfText(pdf, qty, rowX + colWidths[3], ctx.y, {
      fontSize: 8,
      textAlign: 'right',
      fontName: ctx.fontFamily,
      editable: ctx.isEditable,
    });
    rowX += colWidths[3];

    // Remaining
    drawPdfText(
      pdf,
      entry.remainingQuantity.toString(),
      rowX + colWidths[4],
      ctx.y,
      {
        fontSize: 8,
        textAlign: 'right',
        fontName: ctx.fontFamily,
        editable: ctx.isEditable,
      },
    );

    ctx.y += 8;
  });

  // Footer
  const totalPagesCount = pdf.getNumberOfPages();
  for (let i = 1; i <= totalPagesCount; i++) {
    pdf.setPage(i);
    pdf.setFontSize(8);
    pdf.setTextColor(0, 0, 0);
    pdf.setFont(ctx.fontFamily, 'normal');
    const footerText = `${reportTitle} — Page ${i} of ${totalPagesCount}`;
    const footerWidth = pdf.getTextWidth(footerText);
    pdf.text(footerText, PAGE_WIDTH - MARGIN - footerWidth, PAGE_HEIGHT - 10);
  }

  const filename = `Inventory_Report_${
    new Date().toISOString().split('T')[0]
  }.pdf`;
  if (returnPDF) return pdf;
  pdf.save(filename);
};
